import 'moment';
import 'chartjs-adapter-moment';
import { Fragment, useEffect, useMemo, useState } from "react";
import { Chart, registerables } from 'chart.js';
import { getLineDatasetsFromPoints } from '../../../services/entity/chart/chart-service';
import { SummaryStats } from '../components/SummaryStats';
import { DATE_RANGES, useDashboard } from "../../../contexts/DashboardContext";
import { FilterBar } from '../components/FilterBar';
import { getChartsPromises } from '../../../services/entity/chart/chart-api';
import { splitCharts } from '../../../services/entity/chart/chart-service';
import { ChartBarIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import { LoaderBlock } from '../components/LoaderBlock';
import { MessageBlock } from '../components/MessageBlock';
import { GenericChart } from '../components/charts/GenericChart';
import { mergeDatasetsByChartId } from '../../../services/entity/chart/chart-service';
import { addHiddenLegendDataset } from '../../../services/dashboard/dashboard-service';
import { cleanParsedData } from '../../../helpers/file/file-helper';
import { BlockLineChart } from "../components/charts/BlockLineChart";
import { PieChart } from "../components/charts/PieChart";
import { useGetData } from "../../../services/api/api-tools";

Chart.register(...registerables);

export const Workstation = () => {
  const {
    charts,
    selectedDateRange,
    setSelectedDateRange,
    workstationSelected,
    setWorkstationSelected,
    workstations,
    workstationPatterns,
    selectedPattern,
    customData,
    setCustomDataCount,
  } = useDashboard();

  const [isSpecificChartsLoading, setIsSpecificChartsLoading] = useState(true);

  // Data for the graph
  const [graphsData, setGraphsData] = useState(null);

  // Contain the data (= InfluxDB points) for each chart
  const [chartsData, setChartsData] = useState([]);

  const patternsData = useGetData('patterns', 'patterns');

  // Split charts into two arrays: 'summaryCharts' for those in the summary and 'specificCharts' for others below
  const [summaryCharts, specificCharts] = useMemo(
    () =>
      splitCharts(
        charts.filter((chart) => chart.workstation?.id === workstationSelected.id),
        'workstation',
      ),
    [charts, workstationSelected],
  );

  const renderSpecificChart = () => {
    if (specificCharts?.length) {
      if (graphsData?.length) {
        return graphsData.map((dataset) => {
          const width = 100 / graphsData.length;
          const style = { width: `${width}%` };
          return (
            <div key={dataset.chartId} style={style}>
              <GenericChart
                type='main-graph'
                charts={specificCharts}
                graphsData={dataset}
                isLoading={isSpecificChartsLoading}
                selectedDateRange={selectedDateRange}
                setSelectedDateRange={setSelectedDateRange}
              />
            </div>
          );
        });
      } else if (isSpecificChartsLoading) {
        return <LoaderBlock />;
      } else {
        return <MessageBlock icon={InformationCircleIcon} message='No logs found for this period' />;
      }
    } else {
      return <MessageBlock icon={ChartBarIcon} message='No graphs saved yet' />;
    }
  };

  // Retrieve the information from our charts and create datasets for the graphs.
  // useEffect(() => {
  //   const fetchData = async () => {
  //     if (specificCharts.length === 0) {
  //       setGraphsData(null);
  //       setIsSpecificChartsLoading(false);
  //       return;
  //     }
  //
  //     setIsSpecificChartsLoading(true);
  //
  //     // We retrieve the raw logs for each chartData in each chart.
  //     // The expected return is an array of objects, where each object represents a dataset with an ID for chart affiliation,
  //     // a chart type, and data points to create the chart curves.
  //     const resolvedData = await getChartsPromises(specificCharts, selectedDateRange, false);
  //
  //     // We format our datasets to be suitable for what the Chart.js library expects for building a graph.
  //     const allDatasets = resolvedData.map((dataset, datasetIndex) => {
  //       return getLineDatasetsFromPoints(dataset, datasetIndex, selectedDateRange);
  //     });
  //
  //     // We merge all datasets sharing the same chart ID to have only one dataset for a graph.
  //     const mergeDatasets = mergeDatasetsByChartId(allDatasets);
  //
  //     // We do the final formatting to have an array of datasets with properties including the dataset and the chartId.
  //     let formattedDataset = Object.entries(mergeDatasets).map(([chartId, datasets]) => ({
  //       chartId: Number(chartId),
  //       datasets,
  //     }));
  //
  //     // If we have custom data in localStorage, we apply it to our charts.
  //     const storedCSVData = localStorage.getItem('custom-csv-data');
  //
  //     if (storedCSVData) {
  //       const parsedData = JSON.parse(storedCSVData);
  //       const cleanedData = cleanParsedData(parsedData);
  //       addHiddenLegendDataset(formattedDataset, cleanedData);
  //       setCustomDataCount(cleanedData.length);
  //     }
  //
  //     setGraphsData(formattedDataset);
  //     setIsSpecificChartsLoading(false);
  //   };
  //
  //   fetchData();
  // }, [specificCharts, selectedDateRange]);

  // If a user uploads new custom data, we edit our charts with this new data.
  // useEffect(() => {
  //   if (customData.length > 0 && graphsData?.length) {
  //     setIsSpecificChartsLoading(true);
  //
  //     let oldData = graphsData;
  //
  //     addHiddenLegendDataset(oldData, customData);
  //
  //     setGraphsData(oldData);
  //
  //     setTimeout(() => {
  //       setIsSpecificChartsLoading(false);
  //     }, 1000);
  //   } else {
  //     let oldData = graphsData;
  //     if (oldData?.length) {
  //       const dataWithoutCustomDataset = oldData.map((chart) => {
  //         return {
  //           ...chart,
  //           datasets: chart.datasets.filter((dataset) => dataset.label !== 'Hidden Legend Dataset'),
  //         };
  //       });
  //       setGraphsData(dataWithoutCustomDataset);
  //     }
  //   }
  // }, [customData]);

  useEffect(() => {
    const fetchData = async () => {
      if (specificCharts.length === 0) {
        setIsSpecificChartsLoading(false);
        setChartsData([]);
        return;
      }

      setIsSpecificChartsLoading(true);

      const resolvedData = await getChartsPromises(specificCharts, selectedDateRange, false);

      setChartsData(resolvedData);
      setIsSpecificChartsLoading(false);
    };

    fetchData();
  }, [specificCharts, selectedDateRange]);

  const patternIndex = workstationPatterns.findIndex(item => item.id === selectedPattern.id);

  // TODO Note to read behind
  // Generally speaking, this component is a WIP component made for demo purposes
  // Something cleaner must be done to work in a production mode
  // Especially concerning patterns management

  const blockChartsData1 = chartsData.filter((c) => c.chart.block === patternIndex * 3 + 1);
  const blockChartsData2 = chartsData.filter((c) => c.chart.block === patternIndex * 3 + 2);
  const blockChartsData3 = chartsData.filter((c) => c.chart.block === patternIndex * 3 + 3);

  return (
    <>
      <FilterBar
        contexts={workstations}
        contextSelected={workstationSelected}
        setContextSelected={setWorkstationSelected}
        isChartLoading={isSpecificChartsLoading}
        graphsData={graphsData}
        isWorkstation
      />
      {/*<SummaryStats summaryCharts={summaryCharts} context='workstation' />*/}
      {/*<div className='w-full h-full pt-4 flex gap-4'>{renderSpecificChart()}</div>*/}
      <div className='w-full h-full flex justify-center pt-4 gap-4'>
        <div className="w-full h-full flex flex-col justify-center gap-4">
          <div className='h-full flex items-center justify-around border-1 border-perception-black-800 text-white p-4 gap-4'>
            {blockChartsData1.sort((a, b) => a.chart.id - b.chart.id).map((chartData) => {
              let value = chartData.value;
              // Apply coefficient in demo mode
              if (process.env.REACT_APP_PRODUCTION_MODE === '2' && chartData.type.startsWith('value')){
                if (selectedDateRange.id === DATE_RANGES.WEEK) value *= 5;
                else if (selectedDateRange.id === DATE_RANGES.MONTH) value *= 20;
                else if (selectedDateRange.id === DATE_RANGES.YEAR) value *= 220;
              }
              return (
                <Fragment key={chartData.chartData.id}>
                  {
                    chartData.type === 'pie' ?
                      "pie"
                      // <BlockLineChart chartData={chartData}/>
                      :
                      <div className="w-1/4 mx-8 p-4">
                        <div className="rounded-md bg-perception-black-800 flex justify-center p-4">
                          <GenericChart type={chartData.type} value={value} />
                        </div>
                        <div className='text-center mt-4 font-semibold h-[50px] flex items-center justify-center text-center'>{chartData.name}</div>
                      </div>
                  }
                </Fragment>
              )
            })}
          </div>
          <div className='h-full flex items-center justify-around border-1 border-perception-black-800 text-white p-4 gap-4'>
            {blockChartsData2.sort((a, b) => b.chart.id - a.chart.id).map((chartData) => {
              let value = chartData.value;
              // Apply coefficient in demo mode
              if (process.env.REACT_APP_PRODUCTION_MODE === '2' && chartData.type.startsWith('value')){
                if (selectedDateRange.id === DATE_RANGES.WEEK) value *= 5;
                else if (selectedDateRange.id === DATE_RANGES.MONTH) value *= 20;
                else if (selectedDateRange.id === DATE_RANGES.YEAR) value *= 220;
              }
              return (
                <Fragment key={chartData.chartData.id}>
                  {
                    chartData.type === 'pie' ?
                      "pie"
                      // <BlockLineChart chartData={chartData}/>
                      :
                      <div className="w-1/4 mx-8 p-4">
                        <div className="rounded-md bg-perception-black-800 flex justify-center p-4">
                          <GenericChart type={chartData.type} value={value} />
                        </div>
                        <div className='text-center mt-4 font-semibold h-[50px] flex items-center justify-center text-center'>{chartData.name}</div>
                      </div>
                  }
                </Fragment>
              )
            })}
          </div>
        </div>
        <div className='w-full h-full border-1 border-perception-black-800 text-white'>
          <div className="bg-perception-black-800 p-4">
            ERRORS DISTRIBUTION
          </div>
          <div className="h-full flex items-center justify-around gap-8 p-4">
            {blockChartsData3.sort((a, b) => b.chart.id - a.chart.id).map((chartData) => {
              let value = chartData.value;
              // Apply coefficient in demo mode
              if (process.env.REACT_APP_PRODUCTION_MODE === '2' && chartData.type.startsWith('value')){
                if (selectedDateRange.id === DATE_RANGES.WEEK) value *= 5;
                else if (selectedDateRange.id === DATE_RANGES.MONTH) value *= 20;
                else if (selectedDateRange.id === DATE_RANGES.YEAR) value *= 220;
              }
              return (
                <Fragment key={chartData.chartData.id}>
                  {
                    chartData.type === 'pie' ?
                      <PieChart chartData={chartData}/>
                      :
                      <div className="w-1/4 mx-8 p-4">
                        <div className="rounded-md bg-perception-black-800 flex justify-center p-4">
                          <GenericChart type={chartData.type} value={value} />
                        </div>
                        <div className='text-center mt-4 font-semibold h-[50px] flex items-center justify-center text-center'>{chartData.name}</div>
                      </div>
                  }
                </Fragment>
              )
            })}
          </div>
        </div>
      </div>
    </>
  );
};
